import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { AuthField } from "@/components/AuthField";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { CADGridBackground } from "@/components/CADGridBackground";
import { HUDSelect } from "@/components/HUDSelect";
import { useProjectStore } from "@/store/projectStore";
import { PROVINCES, type Province } from "@/types";

const EASE = [0.22, 1, 0.36, 1] as const;

const item = {
  hidden: { opacity: 0, y: 18 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 + i * 0.08, duration: 0.6, ease: EASE },
  }),
};

export default function CreateProjectScreen() {
  const navigate = useNavigate();
  const createProject = useProjectStore((s) => s.createProject);
  const [name, setName] = useState("");
  const [province, setProvince] = useState<Province | null>(null);
  const [error, setError] = useState<string>();
  const [leaving, setLeaving] = useState(false);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      setError("Give your project a name");
      return;
    }
    createProject(name.trim(), province);
    setLeaving(true);
    window.setTimeout(() => navigate("/dashboard", { replace: true }), 320);
  };

  return (
    <div
      data-testid="create-project-screen"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-obsidian px-6 py-12"
    >
      <CADGridBackground opacity={0.28} />
      <div
        aria-hidden
        className="absolute left-1/2 top-[40%] h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-safety/[0.05] blur-3xl"
      />

      <AnimatePresence>
        {!leaving && (
          <motion.form
            onSubmit={onSubmit}
            noValidate
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.3, ease: "easeIn" } }}
            className="relative flex w-full max-w-sm flex-col gap-8"
          >
            <motion.div variants={item} custom={0}>
              <BuildEaseLogo height={36} />
            </motion.div>

            <div className="flex flex-col gap-2">
              <div className="overflow-hidden">
                <motion.h1
                  data-testid="create-project-headline"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
                  className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl"
                >
                  Start your build
                </motion.h1>
              </div>
              <motion.p
                variants={item}
                custom={2}
                data-testid="create-project-subtext"
                className="font-sans text-sm text-white/50"
              >
                Give your project a name to begin.
              </motion.p>
            </div>

            <div className="flex flex-col gap-4">
              <motion.div variants={item} custom={3}>
                <AuthField
                  label="Project Name"
                  index="01"
                  testId="project-name-input"
                  type="text"
                  autoFocus
                  autoComplete="off"
                  placeholder="e.g. My Family Home"
                  value={name}
                  error={error}
                  onChange={(e) => { setName(e.target.value); setError(undefined); }}
                />
              </motion.div>
              <motion.div variants={item} custom={4}>
                <HUDSelect
                  label="Province"
                  index="02"
                  testId="project-province-select"
                  options={PROVINCES}
                  value={province}
                  onChange={setProvince}
                  placeholder="Where are you building?"
                  optional
                />
              </motion.div>
            </div>

            <motion.div variants={item} custom={5}>
              <motion.button
                type="submit"
                data-testid="create-project-submit-button"
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 24 }}
                className="group relative flex h-14 w-full items-center justify-center overflow-hidden rounded-2xl bg-safety font-display text-base font-semibold text-white shadow-[0_12px_40px_-12px_rgba(255,91,0,0.7)]"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
                />
                <span className="relative flex items-center gap-2">
                  Create Project
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </motion.button>
            </motion.div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
